import { useScienceTutorSection } from './scienceTutorContext';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Layers3,
  Lightbulb,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ElementLegend, MoleculeDiagram } from './MatterClassifierDiagram';
import { FormulaCountPanel, FreeFormulaPanel, MatchPanel } from './MatterClassifierFormulas';
import { PureMixturePanel } from './MatterClassifierPureMixture';
import {
  CLASSIFY_CASES,
  CONDITIONS_NOTE,
  EVIDENCE_QUESTIONS,
  MATTER_CLASSIFIER_MODES,
  MATTER_CLASSIFIER_SOURCES,
  MATTER_CLASS_LABELS,
  SPECIES,
  caseFacts,
  checkClassification,
  checkEvidence,
  layoutDiagram,
  type ClassifierMode,
  type EvidenceQuestion,
  type MatterClass,
} from './matterClassifier';
import { StudyShell } from './StudyShell';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const CLASS_OPTIONS: MatterClass[] = ['element', 'compound', 'mixture'];

const CLASS_HINTS: Record<MatterClass, string> = {
  element: 'Only one kind of atom',
  compound: 'Different atoms chemically joined, all particles the same',
  mixture: 'More than one kind of particle',
};

function yesNo(value: boolean) {
  return value ? 'Yes' : 'No';
}

function ClassifyPanel() {
  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState<MatterClass | null>(null);
  const [evidence, setEvidence] = useState<Partial<Record<EvidenceQuestion, boolean>>>({});
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('ClassifyPanel', { index, choice, evidence, checked });
  const item = CLASSIFY_CASES[index];
  const layout = useMemo(() => layoutDiagram(item), [item]);
  const facts = caseFacts(item);
  const result = checked && choice ? checkClassification(item, choice) : null;
  const description = item.components
    .map((component) => {
      const species = SPECIES[component.speciesId];
      return species?.lattice ? `a ${species.name}` : `${component.count} × ${species?.name ?? 'unknown particle'}`;
    })
    .join(' and ');

  const go = (next: number) => {
    setIndex(next);
    setChoice(null);
    setEvidence({});
    setChecked(false);
  };

  return (
    <section className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8" aria-labelledby="classify-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Year 8 · Particle diagrams</p>
      <h2 id="classify-heading" className="mt-2 text-2xl font-extrabold text-ink">Element, compound or mixture?</h2>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="button" aria-label="Previous case" disabled={index === 0} onClick={() => go(index - 1)} className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <select
            aria-label="Choose a case"
            value={item.id}
            onChange={(event) => go(CLASSIFY_CASES.findIndex((candidate) => candidate.id === event.target.value))}
            className="w-full rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-extrabold text-ink"
          >
            {CLASSIFY_CASES.map((candidate, position) => (
              <option key={candidate.id} value={candidate.id}>
                {position + 1}. {candidate.title}
              </option>
            ))}
          </select>
        </div>
        <button type="button" aria-label="Next case" disabled={index >= CLASSIFY_CASES.length - 1} onClick={() => go(index + 1)} className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40">
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold text-ink">{item.title}</h3>
          <p className="mt-1 text-sm font-medium leading-6 text-slate2">{item.context}</p>
          <div className="mt-3">
            <MoleculeDiagram atoms={layout.atoms} bonds={layout.bonds} label={`Particle diagram: ${description}`} />
          </div>
          <div className="mt-3">
            <ElementLegend elements={facts?.elements ?? []} />
          </div>
        </div>

        <div className="min-w-0">
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">Your classification</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {CLASS_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={checked}
                  aria-pressed={choice === option}
                  onClick={() => setChoice(option)}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors disabled:cursor-default ${
                    choice === option ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
                  }`}
                >
                  {MATTER_CLASS_LABELS[option]}
                </button>
              ))}
            </div>
            <dl className="mt-2 grid gap-1 text-xs font-medium leading-5 text-slate2">
              {CLASS_OPTIONS.map((option) => (
                <div key={option} className="flex gap-1">
                  <dt className="font-extrabold text-ink-soft">{MATTER_CLASS_LABELS[option]}:</dt>
                  <dd>{CLASS_HINTS[option]}</dd>
                </div>
              ))}
            </dl>
          </fieldset>

          <fieldset className="mt-5">
            <legend className="text-sm font-extrabold text-ink">Explain why (look at the particles)</legend>
            <div className="mt-2 space-y-2">
              {EVIDENCE_QUESTIONS.map((question) => (
                <div key={question.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-hairline bg-surface px-3 py-2">
                  <span className="text-sm font-bold text-ink-soft">{question.label}</span>
                  <span className="flex gap-1" role="group" aria-label={question.label}>
                    {[true, false].map((value) => (
                      <button
                        key={String(value)}
                        type="button"
                        disabled={checked}
                        aria-pressed={evidence[question.id] === value}
                        onClick={() => setEvidence((current) => ({ ...current, [question.id]: value }))}
                        className={`rounded-full px-3 py-1 text-xs font-extrabold ${evidence[question.id] === value ? 'bg-ink text-white' : 'bg-canvas-pure text-slate2'}`}
                      >
                        {yesNo(value)}
                      </button>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </fieldset>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!choice || checked}
              onClick={() => setChecked(true)}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Check classification
            </button>
            <button type="button" onClick={() => go(index)} className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface">
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again
            </button>
          </div>

          {result && facts ? (
            <div
              role="status"
              aria-label="Classification feedback"
              className={`mt-4 rounded-[1.5rem] border p-5 ${result.correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
            >
              <div className="flex items-start gap-3">
                {result.correct ? (
                  <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
                ) : (
                  <Lightbulb className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
                )}
                <div>
                  <p className="font-extrabold text-ink">
                    {result.correct ? `Correct — ${MATTER_CLASS_LABELS[result.expected]}` : `Not quite — this is ${result.expected === 'element' ? 'an' : 'a'} ${MATTER_CLASS_LABELS[result.expected].toLowerCase()}`}
                  </p>
                  <p className="mt-2 font-medium leading-7 text-slate2">{result.feedback}</p>
                </div>
              </div>
              <ul className="mt-4 grid gap-2">
                {EVIDENCE_QUESTIONS.map((question) => {
                  const answered = evidence[question.id];
                  const match = answered === undefined ? null : checkEvidence(item, question.id, answered);
                  return (
                    <li key={question.id} className="rounded-2xl bg-white/75 px-4 py-2 text-sm font-bold text-ink-soft">
                      {question.label} Model: <strong className="text-ink">{yesNo(facts[question.id])}</strong>
                      {answered === undefined ? ' · not answered' : match ? ' · your answer matches' : ` · you said ${yesNo(answered).toLowerCase()}`}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function MatterClassifierPage() {
  const year = useChemistryToolYear();
  const [manualMode, setManualMode] = useState<ClassifierMode | null>(null);
  const mode = manualMode ?? modeForYear(MATTER_CLASSIFIER_MODES, year);

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'matter-classifier', state: { manualMode } }}>
      <Link to="/learn/study/subjects/chemistry/tools" className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-sunshine p-6 shadow-card-soft lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Layers3 className="h-4 w-4" aria-hidden="true" /> Matter &amp; change
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Elements, Compounds &amp; Mixtures Sorter
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Decide whether particles show a pure substance or a mixture, sort them into elements,
              compounds and mixtures, then read formulas atom by atom.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Explore tool · Year 7–9
          </div>
        </div>
        <div className="mt-6 grid max-w-3xl gap-1 rounded-[1.5rem] bg-white/75 p-1 sm:grid-cols-3 sm:rounded-full" aria-label="Choose learning level">
          {MATTER_CLASSIFIER_MODES.map((option) => (
            <button
              key={option.mode}
              type="button"
              aria-pressed={mode === option.mode}
              onClick={() => setManualMode(option.mode)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option.mode ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mt-6 space-y-6">
        {mode === 'pure-mixture' ? <PureMixturePanel /> : null}
        {mode === 'classify' ? <ClassifyPanel /> : null}
        {mode === 'formulas' ? (
          <>
            <FormulaCountPanel />
            <MatchPanel />
            <FreeFormulaPanel />
          </>
        ) : null}
      </div>

      <footer className="mt-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-sm font-medium leading-6 text-slate2">
        <p>
          Model boundary: {CONDITIONS_NOTE} Colours are a key only; the symbol on each atom is the
          label. Hydrate formulas and ions are not supported. Nothing on this page is saved or scored.
        </p>
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer font-extrabold text-ink">Sources (awaiting teaching review)</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {MATTER_CLASSIFIER_SOURCES.map((source) => (
              <li key={source.id}>{source.citation}</li>
            ))}
          </ul>
        </details>
      </footer>
    </StudyShell>
  );
}
